// Raise an Academy request: what the money is for, line by line, and where it
// is paid, then send the member a page to sign on SignNow.
//
// Everything the member reads is written by Postgres (academy_request_document_
// build, db/migrations/0367). Preview shows the exact page and refuses in the
// same sentences Send would. Send saves first, so a SignNow refusal leaves a
// "Not sent" request to send again rather than nothing.
//
// The running total here is only for the person typing. The total on the page
// the member signs is the one Postgres adds up.
import { useEffect, useMemo, useState } from 'react'
import { Eye, Plus, Send, Trash2 } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../lib/shadcn/dialog'
import { Input } from '../../lib/shadcn/input'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { useCreateAcademyRequest, useGetAcademyRequestPreview } from '../../hooks/backend/academy'
import { formatZar, localPhone, pdfBlobUrl } from '../../utils/loans'
import type { AcademyMember, RequestOptions } from '../../utils/academy'

type Line = { kind: string; amount_zar: string; detail: string }
type Sent = { ok: boolean; ref: string; sent: boolean; signer_email?: string | null; send_error?: string }

const blankLine = (): Line => ({ kind: '', amount_zar: '', detail: '' })

export function RaiseAcademyRequestDialog({
  open, onOpenChange, member, options, onSaved,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  member: AcademyMember
  options: RequestOptions | null
  onSaved: () => void
}) {
  const [lines, setLines] = useState<Line[]>([blankLine()])
  const [method, setMethod] = useState<'ewallet' | 'bank'>('ewallet')
  const [ewallet, setEwallet] = useState('')
  const [bank, setBank] = useState('')
  const [holder, setHolder] = useState('')
  const [account, setAccount] = useState('')
  const [branch, setBranch] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [sent, setSent] = useState<Sent | null>(null)
  const preview = useGetAcademyRequestPreview<{ pdf_base64: string }>()
  const create = useCreateAcademyRequest<Sent>()
  const busy = preview.loading || create.loading

  useEffect(() => {
    if (!open) return
    setLines([blankLine()])
    setMethod('ewallet')
    setEwallet(localPhone(member.primary_phone ?? member.whatsapp_phone))
    setBank('')
    setHolder(member.full_name ?? '')
    setAccount('')
    setBranch('')
    setNote('')
    setError(null)
    setPreviewUrl(null)
    setSent(null)
  // Keyed on the person, not the member object. The board refreshes behind an
  // open dialog and hands it a new copy of the same person; keying on the object
  // wiped everything Musa had typed. Found opening the screen, 30 September 2026.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, member.intake_id])

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl) }, [previewUrl])

  const kinds = options?.kinds ?? []
  const groups = useMemo(() => {
    const out: { group: string; kinds: typeof kinds }[] = []
    for (const k of kinds) {
      const g = out.find((x) => x.group === k.group)
      if (g) g.kinds.push(k)
      else out.push({ group: k.group, kinds: [k] })
    }
    return out
  }, [kinds])

  const touched = () => { setPreviewUrl(null); setError(null) }
  const setLine = (i: number, patch: Partial<Line>) => {
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)))
    touched()
  }
  const runningTotal = lines.reduce((s, l) => s + (Number(l.amount_zar.replace(/\s/g, '').replace(',', '.')) || 0), 0)

  const body = {
    intake_id: member.intake_id,
    items: lines.map((l) => ({ kind: l.kind, amount_zar: l.amount_zar.replace(/\s/g, '').replace(',', '.'), detail: l.detail })),
    note,
    payout_method: method,
    ewallet_number: method === 'ewallet' ? ewallet : null,
    bank_name: method === 'bank' ? bank : null,
    account_holder: method === 'bank' ? holder : null,
    account_number: method === 'bank' ? account : null,
    branch_code: method === 'bank' ? branch : null,
  }

  async function showPreview() {
    setError(null)
    try {
      const r = await preview.trigger(body, { skipCache: true })
      if (r?.pdf_base64) setPreviewUrl(pdfBlobUrl(r.pdf_base64))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  async function send() {
    setError(null)
    try {
      const r = await create.trigger(body)
      if (r) { setSent(r); onSaved() }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  if (sent) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{sent.sent ? `${sent.ref} is with ${member.full_name ?? 'them'}` : `${sent.ref} is saved but not sent`}</DialogTitle>
            <DialogDescription>
              {sent.sent
                ? `SignNow has emailed ${sent.signer_email ?? 'them'} the page to sign, and you are copied. Luyanda is emailed the moment they sign, and pays it then.`
                : 'SignNow did not accept it, so they have nothing yet. It is kept as Not sent; open it under Sponsorship and use Send again.'}
            </DialogDescription>
          </DialogHeader>
          {!sent.sent && sent.send_error && (
            <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">{sent.send_error}</p>
          )}
          <div className="flex justify-end"><Button variant="brand" onClick={() => onOpenChange(false)}>Done</Button></div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o) }}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Academy request for {member.full_name ?? 'this member'}</DialogTitle>
          <DialogDescription>
            They get a page to sign on SignNow, and Fleetly pays once they have. It is a sponsorship: they owe nothing unless they
            become a Fleetly driver, and then it comes off their pay over {options?.recovery_instalments ?? 3} payslips.
          </DialogDescription>
        </DialogHeader>

        <section className="space-y-2">
          <span className="text-xs font-medium text-foreground">What the money is for</span>
          {lines.map((l, i) => {
            const kind = kinds.find((k) => k.code === l.kind)
            return (
              <div key={i} className="grid grid-cols-[1fr_7rem_2rem] items-start gap-2 sm:grid-cols-[14rem_7rem_1fr_2rem]">
                <NativeSelect value={l.kind} onChange={(e) => setLine(i, { kind: e.target.value })} aria-label={`Line ${i + 1}: what it is for`}>
                  <option value="">Choose</option>
                  {groups.map((g) => (
                    <optgroup key={g.group} label={g.group}>
                      {g.kinds.map((k) => <option key={k.code} value={k.code}>{k.label}</option>)}
                    </optgroup>
                  ))}
                </NativeSelect>
                <Input inputMode="decimal" placeholder="Rand" value={l.amount_zar} aria-label={`Line ${i + 1}: amount`}
                  onChange={(e) => setLine(i, { amount_zar: e.target.value })} />
                <Input className="col-span-3 row-start-2 sm:col-span-1 sm:row-start-auto"
                  placeholder={kind?.needs_detail ? 'Say what it is for (printed on the page)' : 'Detail (optional)'}
                  value={l.detail} aria-label={`Line ${i + 1}: detail`} onChange={(e) => setLine(i, { detail: e.target.value })} />
                <Button variant="ghost" size="icon" className="h-9 w-8" aria-label={`Remove line ${i + 1}`} disabled={lines.length === 1}
                  onClick={() => { setLines((ls) => ls.filter((_, j) => j !== i)); touched() }}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )
          })}
          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" disabled={lines.length >= 10} onClick={() => { setLines((ls) => [...ls, blankLine()]); touched() }}>
              <Plus className="mr-1 h-4 w-4" /> Add a line
            </Button>
            <span className="text-sm text-muted-foreground">Total so far <span className="font-semibold tabular-nums text-foreground">{formatZar(runningTotal)}</span></span>
          </div>
        </section>

        <section className="space-y-2 rounded-md border border-border p-3">
          <span className="text-xs font-medium text-foreground">Where Luyanda pays it</span>
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2"><input type="radio" name="payout" checked={method === 'ewallet'} onChange={() => { setMethod('ewallet'); touched() }} /> eWallet to a cell number</label>
            <label className="flex items-center gap-2"><input type="radio" name="payout" checked={method === 'bank'} onChange={() => { setMethod('bank'); touched() }} /> Into a bank account</label>
          </div>
          {method === 'ewallet' ? (
            <div className="space-y-1">
              <label htmlFor="ar-ewallet" className="text-xs font-medium text-foreground">Cell number for the eWallet</label>
              <Input id="ar-ewallet" inputMode="tel" placeholder="0821234567" value={ewallet} onChange={(e) => { setEwallet(e.target.value); touched() }} />
              <p className="text-[11px] text-muted-foreground">Filled from their record. Change it if the money goes to another number.</p>
            </div>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="space-y-1">
                <label htmlFor="ar-bank" className="text-xs font-medium text-foreground">Bank</label>
                <NativeSelect id="ar-bank" value={bank} onChange={(e) => { setBank(e.target.value); touched() }}>
                  <option value="">Choose the bank</option>
                  {(options?.banks ?? []).map((b) => <option key={b} value={b}>{b}</option>)}
                </NativeSelect>
              </div>
              <div className="space-y-1">
                <label htmlFor="ar-holder" className="text-xs font-medium text-foreground">Account in the name of</label>
                <Input id="ar-holder" value={holder} onChange={(e) => { setHolder(e.target.value); touched() }} />
              </div>
              <div className="space-y-1">
                <label htmlFor="ar-account" className="text-xs font-medium text-foreground">Account number</label>
                <Input id="ar-account" inputMode="numeric" value={account} onChange={(e) => { setAccount(e.target.value); touched() }} />
              </div>
              <div className="space-y-1">
                <label htmlFor="ar-branch" className="text-xs font-medium text-foreground">Branch code (optional)</label>
                <Input id="ar-branch" inputMode="numeric" value={branch} onChange={(e) => { setBranch(e.target.value); touched() }} />
              </div>
            </div>
          )}
        </section>

        <div className="space-y-1">
          <label htmlFor="ar-note" className="text-xs font-medium text-foreground">Note for Luyanda (optional, not on the page they sign)</label>
          <Input id="ar-note" value={note} onChange={(e) => { setNote(e.target.value); touched() }} placeholder="e.g. Medical booked for Friday at the Soweto clinic" />
        </div>

        {previewUrl && (
          <div className="space-y-2">
            <iframe title="The page they sign" src={previewUrl} className="hidden h-[28rem] w-full rounded-md border border-border sm:block" />
            <a href={previewUrl} target="_blank" rel="noreferrer" className="text-xs underline underline-offset-2">Open the page in a new tab</a>
          </div>
        )}
        {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">{error}</p>}

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button variant="outline" onClick={() => void showPreview()} disabled={busy}>
            {preview.loading ? <Spinner className="mr-1 h-4 w-4" /> : <Eye className="mr-1 h-4 w-4" />} Preview the page
          </Button>
          <Button variant="brand" onClick={() => void send()} disabled={busy}>
            {create.loading ? <Spinner className="mr-1 h-4 w-4" /> : <Send className="mr-1 h-4 w-4" />} Send to sign
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
